import * as signalR from '@microsoft/signalr';
import config from '../config';

/** Payloads emitted by the server over the pod SignalR hubs. */
export interface PodMessageCreatedPayload {
  id: string;
  pod_id: string;
  sender_id: string | null;
  display_name: string | null;
  avatar_url: string | null;
  type: 'text' | 'system';
  body: string;
  sent_at: string;
}

export interface PodMemberEventPayload {
  userId: string;
  displayName: string;
  podId: string;
}

type PodHubEventMap = {
  'message.reaction': (payload: { podId: string; messageId: string; userId: string; reactionType: string | null; counts: Record<string, number> }) => void;
  'message.created': (payload: PodMessageCreatedPayload) => void;
  'member.joined': (payload: PodMemberEventPayload) => void;
  'member.left': (payload: PodMemberEventPayload) => void;
  'pod.updated': (payload: { podId: string }) => void;
};

class PodHubService {
  private connection: signalR.HubConnection;
  private starting = false;
  private isStopped = false;
  private restartTimer: ReturnType<typeof setTimeout> | null = null;
  // Read by accessTokenFactory on every (re)connect attempt so a rotated
  // session token is always picked up - the factory closure must never
  // capture a single, possibly stale, token string.
  private authToken: string;
  private reconnectedHandlers: Set<() => void> = new Set();

  constructor(authToken: string) {
    this.authToken = authToken;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${config.apiServerUrl}/podHub`, {
        accessTokenFactory: () => this.authToken,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => {
          const schedule = [0, 2000, 5000, 10000];
          const base = ctx.previousRetryCount < schedule.length
            ? schedule[ctx.previousRetryCount]
            : 15000;
          return base + Math.floor(Math.random() * 2000);
        },
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.connection.serverTimeoutInMilliseconds = 30000;
    this.connection.keepAliveIntervalInMilliseconds = 10000;

    this.connection.onreconnecting(() => console.log('PodHub: Reconnecting…'));
    this.connection.onreconnected(() => {
      console.log('PodHub: Reconnected');
      // Each handler runs in isolation so one faulty subscriber can't stop
      // the rest from running or throw out of the SignalR callback.
      for (const handler of Array.from(this.reconnectedHandlers)) {
        try {
          handler();
        } catch (err) {
          console.error('PodHub: onReconnected handler error', err);
        }
      }
    });
    this.connection.onclose(() => {
      console.log('PodHub: Connection closed');
      if (!this.isStopped) {
        this.scheduleRestart();
      }
    });
  }

  /** Keeps the token used for (re)connect attempts in sync with the session. */
  updateToken(authToken: string): void {
    this.authToken = authToken;
  }

  private scheduleRestart(): void {
    if (this.restartTimer) return;
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (this.isStopped) return;
      this.start().catch(() => {
        if (!this.isStopped) this.scheduleRestart();
      });
    }, 5000);
  }

  async start(): Promise<void> {
    if (
      this.connection.state === signalR.HubConnectionState.Connected ||
      this.connection.state === signalR.HubConnectionState.Connecting ||
      this.starting
    ) {
      return;
    }

    this.starting = true;
    this.isStopped = false;
    try {
      await this.connection.start();
      console.log('PodHub: Connected');
    } catch (err) {
      console.error('PodHub: Start error', err);
      if (!this.isStopped) this.scheduleRestart();
    } finally {
      this.starting = false;
    }
  }

  async stop(): Promise<void> {
    this.isStopped = true;
    if (this.restartTimer) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }
    if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
      await this.connection.stop();
    }
  }

  /**
   * Subscribe to a hub event. Returns an unsubscribe function that removes
   * only this handler, so multiple consumers can listen independently.
   */
  on<K extends keyof PodHubEventMap>(event: K, handler: PodHubEventMap[K]): () => void {
    this.connection.on(event, handler as (...args: unknown[]) => void);
    return () => this.connection.off(event, handler as (...args: unknown[]) => void);
  }

  /**
   * Notifies subscribers whenever the connection is re-established after a
   * drop. Consumers use this to silently resync (re-fetch latest data) so a
   * connection blip never leaves the UI permanently stale.
   */
  onReconnected(handler: () => void): () => void {
    this.reconnectedHandlers.add(handler);
    return () => this.reconnectedHandlers.delete(handler);
  }

  get isConnected(): boolean {
    return this.connection.state === signalR.HubConnectionState.Connected;
  }
}

let _instance: PodHubService | null = null;

export function getPodHubService(authToken: string): PodHubService {
  if (!_instance) {
    _instance = new PodHubService(authToken);
  } else {
    _instance.updateToken(authToken);
  }
  return _instance;
}

export function destroyPodHubService(): void {
  if (_instance) {
    void _instance.stop();
    _instance = null;
  }
}

export type { PodHubService };
